/**
 * The PanelRectRegistry (architecture §7): panels register the element the
 * tour should spotlight for them, and the tour overlay asks for its rect.
 * Keeping this as a registry means the tour never queries the DOM by class
 * name or knows how panels are laid out.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { TourTarget } from "./steps";

export class PanelRectRegistry {
  private readonly elements = new Map<TourTarget, HTMLElement>();

  register(target: TourTarget, element: HTMLElement | null): void {
    if (element) this.elements.set(target, element);
    else this.elements.delete(target);
  }

  /** The target's rect in viewport coordinates, or null if it isn't on screen. */
  rectOf(target: TourTarget): DOMRect | null {
    const element = this.elements.get(target);
    return element?.isConnected ? element.getBoundingClientRect() : null;
  }
}

const Context = createContext<PanelRectRegistry | null>(null);

export function TourTargetsProvider({ children }: { children: ReactNode }) {
  const [registry] = useState(() => new PanelRectRegistry());
  return <Context.Provider value={registry}>{children}</Context.Provider>;
}

export function usePanelRects(): PanelRectRegistry | null {
  return useContext(Context);
}

/** A ref callback that registers an element as a tour target (no-op outside the provider). */
export function useTourTarget(target: TourTarget): (element: HTMLElement | null) => void {
  const registry = usePanelRects();
  const register = useCallback(
    (element: HTMLElement | null) => {
      registry?.register(target, element);
    },
    [registry, target],
  );
  return useMemo(() => register, [register]);
}
